import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Loader from "@jumbo/components/Loader";
import {
  LEAD_STATUSES,
  addLeadNote,
  clearLeadDetail,
  editLead,
  getAssignableUsers,
  getLeadActivities,
  updateLead,
} from "app/redux/slice/leadSlice";
import { LEADS } from "app/routes/constant";
import ChatHistory from "../chat-history/index";
import LeadImages from "../lead-images/index";
import SampleProducts from "../sample-products/index";

const ACTIVITY_LABELS = {
  created: "Lead Created",
  status_changed: "Status Changed",
  assigned: "Lead Assigned",
  note_added: "Note Added",
  image_uploaded: "Image Uploaded",
  image_deleted: "Image Deleted",
};

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    detail,
    detailLoading,
    detailFetched,
    activities,
    activitiesLoading,
    assignableUsers,
  } = useSelector((state) => state?.lead);
  const [tab, setTab] = useState(0);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const loadDetail = () => {
    dispatch(editLead(id));
    dispatch(getLeadActivities(id));
  };

  useEffect(() => {
    loadDetail();
    dispatch(getAssignableUsers());
    return () => {
      dispatch(clearLeadDetail());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const notifyAndRefresh = (response, onSuccess) => {
    const isSuccess = response.status === 200;
    enqueueSnackbar(response.data?.responseMessage, {
      variant: isSuccess ? "success" : "error",
    });
    if (isSuccess) onSuccess?.();
  };

  const handleFieldUpdate = async (data) => {
    const response = await dispatch(updateLead({ id, data })).unwrap();
    notifyAndRefresh(response, loadDetail);
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    const response = await dispatch(
      addLeadNote({ id, data: { message: noteText.trim() } })
    ).unwrap();
    notifyAndRefresh(response, () => {
      setNoteText("");
      loadDetail();
    });
    setSavingNote(false);
  };

  if (detailLoading || !detailFetched) {
    return <Loader />;
  }

  if (!detail) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This lead was not found, or you don't have access to view it.
          </Typography>
          <Button variant="outlined" onClick={() => navigate(LEADS)}>
            Back to List
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <span>{detail.name || detail.phoneNumber}</span>
            {detail.leadNumber && (
              <Chip size="small" label={detail.leadNumber} color="primary" />
            )}
          </Stack>
        }
        subheader={detail.phoneNumber}
        action={
          <Button variant="outlined" onClick={() => navigate(LEADS)}>
            Back to List
          </Button>
        }
      />
      <CardContent>
        <Tabs
          value={tab}
          onChange={(e, newTab) => setTab(newTab)}
          sx={{ mb: 3 }}
        >
          <Tab label="Overview" />
          <Tab label="Notes" />
          <Tab label="Activity Timeline" />
          <Tab label="Chat History" />
          <Tab label="Sample Products" />
        </Tabs>

        {tab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {detail.email || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Address
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {[detail.address, detail.city, detail.state, detail.country]
                  .filter(Boolean)
                  .join(", ") || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Source
              </Typography>
              <Chip size="small" label={detail.source} sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="detail-status-label">Status</InputLabel>
                <Select
                  labelId="detail-status-label"
                  label="Status"
                  value={detail.status}
                  onChange={(e) =>
                    handleFieldUpdate({ status: e.target.value })
                  }
                >
                  {LEAD_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="detail-assigned-label">
                  Assigned To
                </InputLabel>
                <Select
                  labelId="detail-assigned-label"
                  label="Assigned To"
                  value={detail.assignedTo?._id || ""}
                  onChange={(e) =>
                    handleFieldUpdate({ assignedTo: e.target.value })
                  }
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {assignableUsers?.map((userOption) => (
                    <MenuItem key={userOption._id} value={userOption._id}>
                      {userOption.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    color="primary"
                    checked={Boolean(detail.onParty)}
                    onChange={(e) =>
                      handleFieldUpdate({ onParty: e.target.checked })
                    }
                  />
                }
                label="On-Party"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mb: 3 }} />
              <LeadImages
                leadId={id}
                images={detail.images}
                onChanged={loadDetail}
              />
            </Grid>
          </Grid>
        )}

        {tab === 1 && (
          <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Add a note"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <LoadingButton
                variant="contained"
                loading={savingNote}
                loadingIndicator={<CircularProgress size={20} />}
                onClick={handleAddNote}
              >
                Add
              </LoadingButton>
            </Stack>
            {detail.notes?.length ? (
              <Stack spacing={2}>
                {[...detail.notes].reverse().map((note, index) => (
                  <Box
                    key={note._id || index}
                    sx={{ p: 2, bgcolor: "grey.100", borderRadius: 2 }}
                  >
                    <Typography variant="body2">{note.message}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {note.createdBy?.name || "Unknown"} —{" "}
                      {new Date(note.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No notes yet.
              </Typography>
            )}
          </Box>
        )}

        {tab === 2 &&
          (activitiesLoading ? (
            <Loader />
          ) : activities?.length ? (
            <Stack spacing={2}>
              {activities.map((activity) => (
                <Box
                  key={activity._id}
                  sx={{ borderLeft: "3px solid", borderColor: "primary.main", pl: 2 }}
                >
                  <Typography variant="body1">
                    {ACTIVITY_LABELS[activity.action] || activity.action}
                  </Typography>
                  {activity.description && (
                    <Typography variant="body2" color="text.secondary">
                      {activity.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {activity.performedBy?.name || "System"} —{" "}
                    {new Date(activity.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No activity yet.
            </Typography>
          ))}

        {tab === 3 && <ChatHistory leadId={id} />}

        {tab === 4 && (
          <SampleProducts
            leadId={id}
            selectedIds={detail.sampleProductIds}
            onSaved={loadDetail}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default LeadDetail;
